import { ReactComponent as LocationIcon } from 'assets/image/svg/location.svg';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { INITIAL_ZOOM } from 'src/utils/constants';
import { EventPinGraphic } from './EventAnchorLayer';
import 'mapbox-gl/dist/mapbox-gl.css';

/** NPT-2067 — the event or searched place, shown next to the hotel so the distance is visible. */
export type SimpleMapAnchor = { latitude: number; longitude: number; label: string };

type SimpleMapViewProps = {
   iconSize?: number;
   id: string;
   interactive?: boolean;
   latitude: number;
   longitude: number;
   zoom?: number;
   hideAttControl?: boolean;
   markerColor?: string;
   anchor?: SimpleMapAnchor | null;
};

const SimpleMapView: React.FC<SimpleMapViewProps> = ({
   iconSize,
   id,
   interactive,
   latitude,
   longitude,
   zoom = INITIAL_ZOOM,
   hideAttControl = false,
   markerColor,
   anchor,
}) => {
   // Guarding with an early return above the hooks made every hook in this
   // component conditional (react-hooks/rules-of-hooks): a coordinate arriving
   // late changes the hook count between renders, which corrupts React's hook
   // state. Compute the condition here, bail inside each effect, and keep the
   // early return below — after every hook has been called (NPT-1570).
   const hasCoordinates = !!latitude && !!longitude;
   // The anchor is only worth showing when it is a different place from the hotel.
   const hasAnchor =
      !!anchor && Number.isFinite(anchor.latitude) && Number.isFinite(anchor.longitude) && (Math.abs(anchor.latitude - latitude) > 1e-4 || Math.abs(anchor.longitude - longitude) > 1e-4);

   const mapRef: any = useRef(null);
   const mapContainerRef: any = useRef(null);

   useEffect(() => {
      if (!hasCoordinates) return;

      mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
      mapRef.current = new mapboxgl.Map({
         container: mapContainerRef.current,
         center: [longitude, latitude],
         zoom: zoom,
         interactive: !!interactive,
         attributionControl: !hideAttControl,
      });

      const el = document.createElement('div');
      el.id = id;
      const roots: ReturnType<typeof ReactDOM.createRoot>[] = [];

      if (iconSize) {
         const root = ReactDOM.createRoot(el);
         roots.push(root);
         root.render(<LocationIcon className={markerColor ? markerColor : 'text-orgPrimary'} width={iconSize} height={iconSize} />);
      }

      new mapboxgl.Marker(el).setLngLat([longitude, latitude]).addTo(mapRef.current!);

      if (hasAnchor && anchor) {
         const anchorEl = document.createElement('div');
         anchorEl.setAttribute('role', 'img');
         anchorEl.setAttribute('aria-label', anchor.label);
         anchorEl.title = anchor.label;
         anchorEl.style.zIndex = '2';
         const anchorRoot = ReactDOM.createRoot(anchorEl);
         roots.push(anchorRoot);
         anchorRoot.render(<EventPinGraphic size={34} />);
         new mapboxgl.Marker({ element: anchorEl, anchor: 'bottom' }).setLngLat([anchor.longitude, anchor.latitude]).addTo(mapRef.current!);
         // Both in view: the hotel and where the traveler is going.
         mapRef.current.fitBounds(
            [
               [Math.min(longitude, anchor.longitude), Math.min(latitude, anchor.latitude)],
               [Math.max(longitude, anchor.longitude), Math.max(latitude, anchor.latitude)],
            ],
            { padding: 80, maxZoom: 15, duration: 0 },
         );
      }

      return () => {
         // Deferred: unmounting a root synchronously during React's own commit warns.
         setTimeout(() => roots.forEach(r => r.unmount()), 0);
         mapRef.current.remove();
      };
   }, [latitude, longitude, hasCoordinates, hasAnchor, anchor?.latitude, anchor?.longitude]);

   useEffect(() => {
      if (!hasCoordinates || hasAnchor) return;

      const center = [longitude, latitude];
      mapRef?.current?.flyTo({ center, zoom: INITIAL_ZOOM, duration: 0 });
   }, [latitude, longitude, hasCoordinates, hasAnchor]);

   if (!hasCoordinates) return null;

   return <div className="flex-1 h-full min-h-[130px] w-full" id="map-container" ref={mapContainerRef} />;
};

export default SimpleMapView;
